<template>
  <PaginatedList :load="load" :query-key="['audit-log', props.entityType, props.entityId]">
    <template #entry="data">
      <div class="mb-2 flex flex-row items-start p-2">
        <div class="mr-4 w-7 shrink-0 rounded-md p-2" :class="colors[data.entry.operation]">
          <component :is="icons[data.entry.operation]" />
        </div>
        <div class="min-w-0 flex-1 py-1">
          <span class="font-bold"> {{ formatDate(data.entry.createdAt) }}</span
          >&nbsp;{{ t('by') }}
          <span v-if="data.entry.user"
            >{{ data.entry.user.firstName }}
            {{ data.entry.user.lastName }}
          </span>
          <span v-else-if="getExternalUserEmail(data.entry.meta)">
            {{ t('externalUser', { email: getExternalUserEmail(data.entry.meta) }) }}
          </span>
          <span v-else class="text-gray-500">
            {{ t('system') }}
          </span>
          <AuditLogOriginInfo :meta="data.entry.meta" />
          &nbsp;
          <RouterLink
            v-if="data.entry.approvalRequest"
            :to="{ name: 'approvalRequest', params: { id: data.entry.approvalRequest.id } }"
            class="text-obsidian-500 hover:underline"
          >
            {{ t('approvalRequest') }}
          </RouterLink>
          <CollapsibleChangedFields
            v-if="data.entry.operation === 'create'"
            :changed-fields="data.entry.changedFields"
          />
          <ChangedFieldsList v-else :changed-fields="data.entry.changedFields" />
          <AuditLogMetaList :meta="data.entry.meta" />
        </div>
      </div>
    </template>
  </PaginatedList>
</template>
<script setup lang="ts">
import PaginatedList from '@/components/PaginatedList.vue';
import { auditLogService } from '@/api/audit-log.service';
import { useI18n } from 'vue-i18n';
import { EnvelopeIcon, PencilIcon, PlusCircleIcon, TrashIcon } from '@heroicons/vue/24/outline';
import ChangedFieldsList from '@/components/audit-log/ChangedFieldsList.vue';
import CollapsibleChangedFields from '@/components/audit-log/CollapsibleChangedFields.vue';
import AuditLogMetaList from '@/components/audit-log/AuditLogMetaList.vue';
import AuditLogOriginInfo from '@/components/audit-log/AuditLogOriginInfo.vue';
import { formatDate } from '@/hooks/use-formatted-date';
import type { AuditLog, AuditLogEntityType } from '@carvia/ros-client-types';
import { RouterLink } from 'vue-router';

const { t } = useI18n();

const icons = {
  create: PlusCircleIcon,
  update: PencilIcon,
  delete: TrashIcon,
  mail_sent: EnvelopeIcon,
};

const colors = {
  create: 'text-green-500 bg-green-100',
  update: 'text-blue-500 bg-blue-100',
  delete: 'text-red-500 bg-red-100',
  mail_sent: 'text-peach-600 bg-peach-100',
};

const props = defineProps<{
  entityType: AuditLogEntityType;
  entityId: string;
}>();

const load = (pageNumber: number) =>
  auditLogService.getForEntity({
    entityType: props.entityType,
    entityId: props.entityId,
    page: pageNumber,
  });

/** Someone who acted without a ROS user, e.g. an External driver (ADR 0021). */
function getExternalUserEmail(meta: AuditLog['meta']): string | undefined {
  return meta?.find((item) => item?.type === 'actor')?.email;
}
</script>

<i18n lang="json">
{
  "en": {
    "by": "by",
    "system": "🤖 system",
    "approvalRequest": "Approval Request",
    "externalUser": "external user {email}"
  },
  "de": {
    "by": "von",
    "system": "🤖 System",
    "approvalRequest": "Genehmigungsanfrage",
    "externalUser": "externer Benutzer {email}"
  },
  "es": {
    "by": "por",
    "system": "🤖 sistema",
    "approvalRequest": "Solicitud de aprobación",
    "externalUser": "usuario externo {email}"
  }
}
</i18n>
